import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import DriveBrowser from '../components/DriveBrowser';
import RichTextEditor from '../components/RichTextEditor';
import { serviziAPI, driveAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  Trash2,
  Briefcase,
  Loader2,
  Euro,
  Search,
  FileText,
  FolderOpen,
  ExternalLink,
  X,
} from 'lucide-react';

const emptyFormData = {
  nome: '',
  descrizione: '',
  prezzo_listino: '',
  documento_id: null,
  documento_nome: null,
  attivita_previste_default: '',
};

const Servizi = () => {
  const [servizi, setServizi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedServizio, setSelectedServizio] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);
  const [saving, setSaving] = useState(false);
  
  // Drive browser state
  const [showDriveBrowser, setShowDriveBrowser] = useState(false);
  const [driveConnected, setDriveConnected] = useState(false);

  useEffect(() => {
    fetchServizi();
    checkDriveConnection();
  }, []);

  const checkDriveConnection = async () => {
    try {
      const response = await driveAPI.status();
      setDriveConnected(response.data.connected === true);
    } catch (error) {
      setDriveConnected(false);
    }
  };

  const fetchServizi = async () => {
    try {
      const response = await serviziAPI.list();
      setServizi(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei servizi');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (servizio = null) => {
    if (servizio) {
      setSelectedServizio(servizio);
      setFormData({
        nome: servizio.nome,
        descrizione: servizio.descrizione || '',
        prezzo_listino: servizio.prezzo_listino.toString(),
        documento_id: servizio.documento_id || null,
        documento_nome: servizio.documento_nome || null,
        attivita_previste_default: servizio.attivita_previste_default || '',
      });
    } else {
      setSelectedServizio(null);
      setFormData(emptyFormData);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      ...formData,
      prezzo_listino: parseFloat(formData.prezzo_listino) || 0,
    };

    try {
      if (selectedServizio) {
        await serviziAPI.update(selectedServizio.id, payload);
        toast.success('Servizio aggiornato');
      } else {
        await serviziAPI.create(payload);
        toast.success('Servizio creato');
      }
      setDialogOpen(false);
      fetchServizi();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await serviziAPI.delete(selectedServizio.id);
      toast.success('Servizio eliminato');
      setDeleteDialogOpen(false);
      fetchServizi();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const openDriveBrowser = () => {
    setShowDriveBrowser(true);
  };

  const selectDocument = (file) => {
    setFormData({
      ...formData,
      documento_id: file.id,
      documento_nome: file.name,
    });
    setShowDriveBrowser(false);
  };

  const removeDocument = () => {
    setFormData({
      ...formData,
      documento_id: null,
      documento_nome: null,
    });
  };

  const filteredServizi = servizi.filter((servizio) =>
    servizio.nome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-48" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="servizi-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">Servizi</h1>
            <p className="text-slate-500 mt-1">Configura i servizi da inserire nei preventivi</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => handleOpenDialog()}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="nuovo-servizio-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Servizio
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {selectedServizio ? 'Modifica Servizio' : 'Nuovo Servizio'}
                </DialogTitle>
                <DialogDescription>
                  Inserisci i dettagli del servizio e associa un documento Google Docs formattato.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome Servizio *</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="es. Sviluppo Sito Web"
                    required
                    data-testid="servizio-nome"
                  />
                </div>
                
                {/* Document Selection */}
                <div className="space-y-2">
                  <Label>Documento Google Docs *</Label>
                  <p className="text-sm text-slate-500 mb-2">
                    Seleziona un documento Google Docs già formattato con la descrizione del servizio.
                  </p>
                  
                  {formData.documento_id ? (
                    <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-blue-900 truncate">{formData.documento_nome}</p>
                        <a
                          href={`https://docs.google.com/document/d/${formData.documento_id}/edit`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                        >
                          Apri in Google Docs <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={removeDocument}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={openDriveBrowser}
                      className="w-full justify-start"
                      disabled={!driveConnected}
                    >
                      <FolderOpen className="w-4 h-4 mr-2" />
                      {driveConnected ? 'Seleziona da Google Drive' : 'Connetti Google Drive nelle Impostazioni'}
                    </Button>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="prezzo_listino">Prezzo di Listino (IVA esclusa) *</Label>
                  <div className="relative">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="prezzo_listino"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.prezzo_listino}
                      onChange={(e) => setFormData({ ...formData, prezzo_listino: e.target.value })}
                      placeholder="0.00"
                      required
                      className="pl-10 price-input"
                      data-testid="servizio-prezzo"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="attivita_previste_default">Dettaglio configurazione servizio (testo di default)</Label>
                  <RichTextEditor
                    value={formData.attivita_previste_default || ''}
                    onChange={(value) => setFormData({ ...formData, attivita_previste_default: value })}
                    placeholder="Inserisci il testo di default per il dettaglio configurazione..."
                    height="150px"
                  />
                  <p className="text-xs text-slate-500">
                    Questo testo HTML apparirà come base nel campo "Dettaglio configurazione" quando selezioni questo servizio nel preventivo.
                  </p>
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving || !formData.documento_id}
                    data-testid="servizio-submit"
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Salvataggio...
                      </>
                    ) : selectedServizio ? (
                      'Aggiorna'
                    ) : (
                      'Crea Servizio'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Cerca servizio..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="servizi-search"
          />
        </div>

        {/* Info */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <p className="text-sm text-blue-800">
              <strong>Come funziona:</strong> Crea un documento Google Docs con la descrizione formattata del servizio 
              (con tabelle, colori, stili). Poi associalo qui. Quando generi un preventivo, il contenuto formattato 
              verrà copiato automaticamente nel documento finale.
            </p>
          </CardContent>
        </Card>

        {/* Services Grid */}
        {filteredServizi.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredServizi.map((servizio) => (
              <Card key={servizio.id} className="border-slate-200 card-hover">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base font-heading line-clamp-2 flex-1" title={servizio.nome}>
                      {servizio.nome}
                    </CardTitle>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(servizio)}
                        data-testid="edit-service-btn"
                        className="h-8 w-8 p-0"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedServizio(servizio);
                          setDeleteDialogOpen(true);
                        }}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Document Badge */}
                    {servizio.documento_id ? (
                      <a
                        href={`https://docs.google.com/document/d/${servizio.documento_id}/edit`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
                      >
                        <FileText className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate max-w-[180px]">{servizio.documento_nome || 'Documento collegato'}</span>
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 border-amber-300">
                        Nessun documento associato
                      </Badge>
                    )}
                    
                    {/* Price */}
                    <div className="pt-3 border-t border-slate-100">
                      <p className="text-2xl font-bold text-orange-600">
                        {formatCurrency(servizio.prezzo_listino)}
                      </p>
                      <p className="text-xs text-slate-500">Prezzo di listino</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-dashed border-2 border-slate-200">
            <CardContent className="py-12 text-center">
              <Briefcase className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">
                {searchTerm ? 'Nessun servizio trovato' : 'Nessun servizio'}
              </h3>
              <p className="text-slate-500 mb-4">
                {searchTerm
                  ? 'Prova a modificare i criteri di ricerca'
                  : 'Inizia creando il tuo primo servizio'}
              </p>
              {!searchTerm && (
                <Button
                  onClick={() => handleOpenDialog()}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Crea Servizio
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Conferma Eliminazione</AlertDialogTitle>
              <AlertDialogDescription>
                Sei sicuro di voler eliminare il servizio "{selectedServizio?.nome}"?
                Questa azione non può essere annullata.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annulla</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Elimina
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Drive Browser - Uses Shared Drives */}
        <DriveBrowser
          open={showDriveBrowser}
          onOpenChange={setShowDriveBrowser}
          onSelect={selectDocument}
          mode="template"
          title="Seleziona Documento Google Docs"
          description="Naviga nei Drive condivisi e seleziona il documento formattato per questo servizio."
        />
      </div>
    </Layout>
  );
};

export default Servizi;
